import { Component } from '@theme/component';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Calendario di prenotazione del giardino.
 *
 * ## Come riflette la disponibilita' reale
 *
 * La sezione punta a un prodotto Shopify ("Prenotazione giardino"). Ogni variante
 * rappresenta una data (opzione con valori tipo `2026-08-15` o `15/08/2026`) e,
 * facoltativamente, un turno / tipologia. La giacenza della variante e' il numero
 * di posti (o tavoli) disponibili per quella data.
 *
 * Liquid serializza le varianti in un `<script type="application/json">`; questo
 * componente le indicizza per giorno e colora il calendario di conseguenza:
 *
 *   - variante non disponibile  -> giorno esaurito, non selezionabile
 *   - giacenza <= soglia        -> "ultimi posti"
 *   - giacenza non tracciata    -> disponibilita' libera
 *
 * Alla conferma viene aggiunta a carrello la variante esatta, quindi la giacenza
 * scala davvero in Shopify: il calendario e l'inventario non possono divergere.
 *
 * In assenza di prodotto configurato il componente lavora in modalita' "richiesta":
 * il calendario resta navigabile (giorni di chiusura e blackout inclusi) e la data
 * scelta viene scritta nel modulo di contatto collegato.
 *
 * @typedef {object} BookingVariant
 * @property {number} id
 * @property {string[]} options
 * @property {boolean} available
 * @property {boolean} tracked
 * @property {number} quantity
 * @property {number} price
 *
 * @typedef {object} BookingDay
 * @property {string} key
 * @property {BookingVariant[]} variants
 * @property {boolean} available
 * @property {number} remaining  Infinity se la giacenza non e' tracciata.
 *
 * @typedef {object} BookingRefs
 * @property {HTMLScriptElement} payload
 * @property {HTMLElement} grid
 * @property {HTMLElement} monthLabel
 * @property {HTMLButtonElement} prevMonth
 * @property {HTMLButtonElement} nextMonth
 * @property {HTMLElement} panel
 * @property {HTMLElement} [slots]
 * @property {HTMLElement} [slotsField]
 * @property {HTMLElement} [summaryDate]
 * @property {HTMLElement} [summaryStock]
 * @property {HTMLElement} [summaryTotal]
 * @property {HTMLInputElement} [quantity]
 * @property {HTMLButtonElement} [submit]
 * @property {HTMLElement} [feedback]
 * @property {HTMLElement} liveRegion
 * @property {HTMLInputElement} [requestDate]
 *
 * @extends Component<BookingRefs>
 */
export class BookingCalendarComponent extends Component {
  requiredRefs = ['grid', 'monthLabel', 'prevMonth', 'nextMonth', 'liveRegion'];

  /** @type {Map<string, BookingDay>} */
  #days = new Map();

  /** @type {any} */
  #payload = null;

  /** @type {number} */
  #dateOptionIndex = -1;

  /** @type {Date} */
  #cursor = new Date();

  /** @type {Date} */
  #minDate = new Date();

  /** @type {Date} */
  #maxDate = new Date();

  /** @type {string | null} */
  #selectedDate = null;

  /** @type {string | null} */
  #selectedSlot = null;

  /** @type {Set<number>} */
  #closedWeekdays = new Set();

  /** @type {Set<string>} */
  #closedDates = new Set();

  /** @type {Intl.DateTimeFormat} */
  #longDate;

  /** @type {Intl.NumberFormat | null} */
  #money = null;

  /** @type {boolean} */
  #submitting = false;

  connectedCallback() {
    super.connectedCallback();

    this.#readConfig();
    this.#readPayload();
    this.#bindEvents();
    this.renderMonth();
  }

  /* ---------------------------------------------------------------- *
   * Configurazione
   * ---------------------------------------------------------------- */

  get locale() {
    return this.dataset.locale || document.documentElement.lang || 'it';
  }

  get lowThreshold() {
    return Number(this.dataset.lowThreshold) || 4;
  }

  get isRequestMode() {
    return this.dataset.mode === 'request' || !this.#payload?.variants?.length;
  }

  #readConfig() {
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);

    const notice = Number(this.dataset.minNoticeDays) || 0;
    this.#minDate = new Date(startOfToday);
    this.#minDate.setDate(this.#minDate.getDate() + notice);

    const monthsAhead = Number(this.dataset.monthsAhead) || 6;
    this.#maxDate = new Date(startOfToday.getFullYear(), startOfToday.getMonth() + monthsAhead + 1, 0);

    this.#cursor = new Date(this.#minDate.getFullYear(), this.#minDate.getMonth(), 1);

    for (const value of (this.dataset.closedWeekdays || '').split(',')) {
      const day = Number(value.trim());
      if (!Number.isNaN(day) && value.trim() !== '') this.#closedWeekdays.add(day);
    }

    for (const value of (this.dataset.closedDates || '').split(/[\s,;]+/)) {
      const parsed = parseDateValue(value);
      if (parsed) this.#closedDates.add(parsed);
    }

    this.#longDate = new Intl.DateTimeFormat(this.locale, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });

    const currency = this.dataset.currency;
    if (currency) {
      try {
        this.#money = new Intl.NumberFormat(this.locale, { style: 'currency', currency });
      } catch {
        this.#money = null;
      }
    }
  }

  #readPayload() {
    const raw = this.refs.payload?.textContent;
    if (!raw) return;

    try {
      this.#payload = JSON.parse(raw);
    } catch (error) {
      console.warn('[booking-calendar] payload non valido', error);
      return;
    }

    const variants = /** @type {BookingVariant[]} */ (this.#payload?.variants ?? []);
    if (!variants.length) return;

    this.#dateOptionIndex = detectDateOptionIndex(this.#payload);
    if (this.#dateOptionIndex < 0) {
      console.warn('[booking-calendar] nessuna opzione con valori data riconoscibili');
      this.#payload = null;
      return;
    }

    for (const variant of variants) {
      const key = parseDateValue(variant.options[this.#dateOptionIndex] ?? '');
      if (!key) continue;

      let day = this.#days.get(key);
      if (!day) {
        day = { key, variants: [], available: false, remaining: 0 };
        this.#days.set(key, day);
      }

      day.variants.push(variant);

      if (!variant.available) continue;
      day.available = true;
      day.remaining = variant.tracked ? day.remaining + Math.max(0, variant.quantity) : Infinity;
    }

    // Un giorno con almeno una variante non tracciata e' sempre "libero".
    for (const day of this.#days.values()) {
      if (day.variants.some((variant) => variant.available && !variant.tracked)) day.remaining = Infinity;
    }
  }

  #bindEvents() {
    this.refs.prevMonth.addEventListener('click', () => this.shiftMonth(-1));
    this.refs.nextMonth.addEventListener('click', () => this.shiftMonth(1));

    this.refs.grid.addEventListener('click', (event) => {
      const button = /** @type {HTMLElement | null} */ (event.target)?.closest?.('button[data-date]');
      if (button instanceof HTMLButtonElement && !button.disabled) this.selectDate(button.dataset.date ?? '');
    });

    this.refs.grid.addEventListener('keydown', (event) => this.#handleGridKeydown(event));

    this.refs.slots?.addEventListener('click', (event) => {
      const button = /** @type {HTMLElement | null} */ (event.target)?.closest?.('button[data-slot]');
      if (button instanceof HTMLButtonElement && !button.disabled) this.selectSlot(button.dataset.slot ?? '');
    });

    this.refs.quantity?.addEventListener('input', () => this.#renderSummary());
    this.refs.submit?.addEventListener('click', (event) => {
      event.preventDefault();
      this.submit();
    });

    // Invio da tastiera (Invio in un campo): il modulo di prenotazione non deve
    // mai partire in modo nativo. In modalita' richiesta il form e' quello di
    // contatto Shopify (classe .booking-cal__request) e deve restare nativo.
    this.querySelector('form.booking-cal__form')?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.submit();
    });
  }

  /* ---------------------------------------------------------------- *
   * Navigazione
   * ---------------------------------------------------------------- */

  /** @param {number} delta */
  shiftMonth(delta) {
    const next = new Date(this.#cursor.getFullYear(), this.#cursor.getMonth() + delta, 1);
    if (next < new Date(this.#minDate.getFullYear(), this.#minDate.getMonth(), 1)) return;
    if (next > new Date(this.#maxDate.getFullYear(), this.#maxDate.getMonth(), 1)) return;

    this.#cursor = next;
    this.renderMonth();
    this.#announce(this.refs.monthLabel.textContent ?? '');
  }

  renderMonth() {
    const year = this.#cursor.getFullYear();
    const month = this.#cursor.getMonth();

    const monthLabel = new Intl.DateTimeFormat(this.locale, { month: 'long', year: 'numeric' }).format(this.#cursor);
    this.refs.monthLabel.textContent = monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1);

    this.refs.prevMonth.disabled =
      new Date(year, month - 1, 1) < new Date(this.#minDate.getFullYear(), this.#minDate.getMonth(), 1);
    this.refs.nextMonth.disabled =
      new Date(year, month + 1, 1) > new Date(this.#maxDate.getFullYear(), this.#maxDate.getMonth(), 1);

    const firstOfMonth = new Date(year, month, 1);
    // La settimana inizia di lunedi': getDay() restituisce 0 per domenica.
    const leading = (firstOfMonth.getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = Math.ceil((leading + daysInMonth) / 7) * 7;

    const fragment = document.createDocumentFragment();

    for (let index = 0; index < cells; index += 1) {
      const dayNumber = index - leading + 1;
      const inMonth = dayNumber >= 1 && dayNumber <= daysInMonth;

      const cell = document.createElement('div');
      cell.className = 'booking-cal__cell';

      if (!inMonth) {
        cell.classList.add('is-outside');
        cell.setAttribute('aria-hidden', 'true');
        fragment.appendChild(cell);
        continue;
      }

      const date = new Date(year, month, dayNumber);
      const key = toKey(date);
      const state = this.#stateFor(date, key);

      const button = document.createElement('button');
      button.type = 'button';
      button.className = `booking-cal__day is-${state.status}`;
      button.dataset.date = key;
      button.disabled = state.status !== 'available' && state.status !== 'low';
      button.setAttribute('aria-label', `${this.#longDate.format(date)} — ${state.label}`);
      if (this.#selectedDate === key) {
        button.classList.add('is-selected');
        button.setAttribute('aria-current', 'date');
      }
      button.tabIndex = this.#selectedDate === key || (!this.#selectedDate && !button.disabled) ? 0 : -1;

      const number = document.createElement('span');
      number.className = 'booking-cal__number';
      number.textContent = String(dayNumber);
      button.appendChild(number);

      const dot = document.createElement('span');
      dot.className = 'booking-cal__dot';
      dot.setAttribute('aria-hidden', 'true');
      button.appendChild(dot);

      if (state.status === 'low' && Number.isFinite(state.remaining)) {
        const badge = document.createElement('span');
        badge.className = 'booking-cal__badge';
        badge.textContent = String(state.remaining);
        button.appendChild(badge);
      }

      cell.appendChild(button);
      fragment.appendChild(cell);
    }

    this.refs.grid.replaceChildren(fragment);
  }

  /**
   * @param {Date} date
   * @param {string} key
   * @returns {{ status: 'past' | 'closed' | 'soldout' | 'low' | 'available', label: string, remaining: number }}
   */
  #stateFor(date, key) {
    const strings = this.#strings;

    if (date < this.#minDate) return { status: 'past', label: strings.past, remaining: 0 };
    if (date > this.#maxDate) return { status: 'closed', label: strings.closed, remaining: 0 };
    if (this.#closedWeekdays.has(date.getDay())) return { status: 'closed', label: strings.closed, remaining: 0 };
    if (this.#closedDates.has(key)) return { status: 'closed', label: strings.closed, remaining: 0 };

    if (this.isRequestMode) return { status: 'available', label: strings.onRequest, remaining: Infinity };

    const day = this.#days.get(key);
    if (!day) return { status: 'closed', label: strings.closed, remaining: 0 };
    if (!day.available) return { status: 'soldout', label: strings.soldOut, remaining: 0 };

    if (Number.isFinite(day.remaining) && day.remaining <= this.lowThreshold) {
      return { status: 'low', label: strings.low.replace('[count]', String(day.remaining)), remaining: day.remaining };
    }

    return { status: 'available', label: strings.available, remaining: day.remaining };
  }

  /** @param {KeyboardEvent} event */
  #handleGridKeydown(event) {
    const keys = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
    const delta = keys[/** @type {keyof typeof keys} */ (event.key)];
    if (delta === undefined) return;

    const current = /** @type {HTMLElement | null} */ (event.target)?.closest?.('button[data-date]');
    if (!(current instanceof HTMLButtonElement)) return;

    event.preventDefault();

    const buttons = /** @type {HTMLButtonElement[]} */ ([...this.refs.grid.querySelectorAll('button[data-date]')]);
    const index = buttons.indexOf(current);
    const next = buttons[index + delta];

    if (next) {
      next.tabIndex = 0;
      current.tabIndex = -1;
      next.focus();
    } else {
      this.shiftMonth(delta > 0 ? 1 : -1);
      const fallback = /** @type {HTMLButtonElement | null} */ (
        this.refs.grid.querySelector(delta > 0 ? 'button[data-date]' : 'button[data-date]:last-of-type')
      );
      fallback?.focus();
    }
  }

  /* ---------------------------------------------------------------- *
   * Selezione
   * ---------------------------------------------------------------- */

  /** @param {string} key */
  selectDate(key) {
    this.#selectedDate = key;
    this.#selectedSlot = null;
    this.renderMonth();

    const [year, month, day] = key.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const readable = this.#longDate.format(date);

    if (this.refs.summaryDate) this.refs.summaryDate.textContent = readable;
    if (this.refs.requestDate) this.refs.requestDate.value = readable;

    this.refs.panel?.setAttribute('data-state', 'ready');
    this.#renderSlots();
    this.#renderSummary();
    this.#clearFeedback();
    this.#announce(`${readable}. ${this.#strings.selected}`);

    if (this.isRequestMode) {
      const targetId = this.dataset.requestTarget;
      if (targetId) document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  /**
   * Incrementa o decrementa la quantita' restando nei limiti della giacenza.
   * Invocato dagli attributi `on:click="/stepQuantity/1"` dei pulsanti +/-.
   *
   * @param {number} delta
   */
  stepQuantity(delta) {
    const input = this.refs.quantity;
    if (!input) return;

    const min = Number(input.min) || 1;
    const max = Number(input.max) || Number(this.dataset.maxQuantity) || 20;
    const next = Math.min(max, Math.max(min, (Number(input.value) || min) + Number(delta)));

    input.value = String(next);
    this.#renderSummary();
  }

  /** @param {string} slot */
  selectSlot(slot) {
    this.#selectedSlot = slot;
    this.#renderSlots();
    this.#renderSummary();
    this.#clearFeedback();
  }

  #renderSlots() {
    const container = this.refs.slots;
    if (!container || this.isRequestMode || !this.#selectedDate) return;

    const day = this.#days.get(this.#selectedDate);
    const hasSlotOptions = (this.#payload?.options?.length ?? 0) > 1;

    if (!day || !hasSlotOptions) {
      this.refs.slotsField?.setAttribute('hidden', '');
      // Con una sola opzione la variante e' univoca: la selezioniamo implicitamente.
      this.#selectedSlot = day?.variants.find((variant) => variant.available) ? '' : null;
      return;
    }

    this.refs.slotsField?.removeAttribute('hidden');

    /** @type {Map<string, BookingVariant>} */
    const bySlot = new Map();
    for (const variant of day.variants) {
      const label = variant.options.filter((_, index) => index !== this.#dateOptionIndex).join(' · ');
      const existing = bySlot.get(label);
      if (!existing || (!existing.available && variant.available)) bySlot.set(label, variant);
    }

    // Se nessun turno e' ancora scelto, preselezioniamo il primo disponibile.
    if (this.#selectedSlot === null) {
      for (const [label, variant] of bySlot) {
        if (variant.available) {
          this.#selectedSlot = label;
          break;
        }
      }
    }

    const fragment = document.createDocumentFragment();

    for (const [label, variant] of bySlot) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'booking-cal__slot';
      button.dataset.slot = label;
      button.disabled = !variant.available;
      button.setAttribute('aria-pressed', String(this.#selectedSlot === label));
      if (this.#selectedSlot === label) button.classList.add('is-selected');

      const name = document.createElement('span');
      name.className = 'booking-cal__slot-name';
      name.textContent = label;
      button.appendChild(name);

      const meta = document.createElement('span');
      meta.className = 'booking-cal__slot-meta';
      if (!variant.available) {
        meta.textContent = this.#strings.soldOut;
      } else if (variant.tracked) {
        meta.textContent = this.#strings.spotsLeft.replace('[count]', String(Math.max(0, variant.quantity)));
      } else if (variant.price) {
        meta.textContent = this.#formatMoney(variant.price);
      }
      if (meta.textContent) button.appendChild(meta);

      fragment.appendChild(button);
    }

    container.replaceChildren(fragment);
  }

  /** @returns {BookingVariant | null} */
  get currentVariant() {
    if (this.isRequestMode || !this.#selectedDate) return null;

    const day = this.#days.get(this.#selectedDate);
    if (!day) return null;

    if (!this.#selectedSlot) return day.variants.find((variant) => variant.available) ?? null;

    return (
      day.variants.find(
        (variant) => variant.options.filter((_, index) => index !== this.#dateOptionIndex).join(' · ') === this.#selectedSlot
      ) ?? null
    );
  }

  #renderSummary() {
    if (this.isRequestMode) return;

    const variant = this.currentVariant;
    const quantityInput = this.refs.quantity;

    if (quantityInput) {
      const max = variant && variant.tracked ? Math.max(1, variant.quantity) : Number(this.dataset.maxQuantity) || 20;
      quantityInput.max = String(max);
      if (Number(quantityInput.value) > max) quantityInput.value = String(max);
      if (Number(quantityInput.value) < 1 || Number.isNaN(Number(quantityInput.value))) quantityInput.value = '1';
    }

    const quantity = Number(quantityInput?.value) || 1;

    if (this.refs.summaryStock) {
      if (!variant) {
        this.refs.summaryStock.textContent = this.#strings.soldOut;
      } else if (variant.tracked) {
        this.refs.summaryStock.textContent = this.#strings.spotsLeft.replace(
          '[count]',
          String(Math.max(0, variant.quantity))
        );
      } else {
        this.refs.summaryStock.textContent = this.#strings.available;
      }
    }

    if (this.refs.summaryTotal) {
      this.refs.summaryTotal.textContent = variant ? this.#formatMoney(variant.price * quantity) : '—';
    }

    if (this.refs.submit) this.refs.submit.disabled = !variant || this.#submitting;
  }

  /* ---------------------------------------------------------------- *
   * Invio
   * ---------------------------------------------------------------- */

  async submit() {
    const variant = this.currentVariant;
    if (!variant || this.#submitting) return;

    const form = this.querySelector('form');
    if (form && !form.reportValidity()) return;

    this.#submitting = true;
    if (this.refs.submit) {
      this.refs.submit.disabled = true;
      this.refs.submit.dataset.loading = 'true';
    }

    const quantity = Number(this.refs.quantity?.value) || 1;
    const properties = this.#collectProperties();
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(variant.id), quantity }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: variant.id, quantity, properties }),
      });

      const result = await response.json();

      if (!response.ok || result.status) {
        const message = result.description || result.message || this.#strings.error;
        this.#setFeedback(message, 'error');
        this.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
        deferred.resolve({ cart: null, detail: { didError: true, source: 'booking-calendar-component' } });
        return;
      }

      const cart = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((cartResponse) => cartResponse.json());

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'booking-calendar-component',
          sourceId: this.id,
          itemCount: quantity,
          didError: false,
        },
      });

      this.#setFeedback(this.#strings.success, 'success');
      this.#announce(this.#strings.success);
    } catch (error) {
      console.error('[booking-calendar]', error);
      deferred.reject(error);
      this.#setFeedback(this.#strings.error, 'error');
    } finally {
      this.#submitting = false;
      if (this.refs.submit) {
        this.refs.submit.removeAttribute('data-loading');
        this.refs.submit.disabled = false;
      }
    }
  }

  /** @returns {Record<string, string>} */
  #collectProperties() {
    /** @type {Record<string, string>} */
    const properties = {};

    if (this.#selectedDate) {
      const [year, month, day] = this.#selectedDate.split('-').map(Number);
      properties[this.dataset.propertyDate || 'Data'] = this.#longDate.format(new Date(year, month - 1, day));
    }

    for (const input of this.querySelectorAll('[data-booking-property]')) {
      if (!(input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement)) continue;
      const name = input.dataset.bookingProperty;
      const value = input.value.trim();
      if (name && value) properties[name] = value;
    }

    return properties;
  }

  /* ---------------------------------------------------------------- *
   * Utility
   * ---------------------------------------------------------------- */

  get #strings() {
    return {
      available: this.dataset.stringAvailable || 'Disponibile',
      low: this.dataset.stringLow || 'Ultimi [count] posti',
      soldOut: this.dataset.stringSoldOut || 'Esaurito',
      closed: this.dataset.stringClosed || 'Chiuso',
      past: this.dataset.stringPast || 'Non prenotabile',
      onRequest: this.dataset.stringOnRequest || 'Su richiesta',
      selected: this.dataset.stringSelected || 'Data selezionata',
      spotsLeft: this.dataset.stringSpotsLeft || '[count] posti',
      success: this.dataset.stringSuccess || 'Prenotazione aggiunta al carrello.',
      error: this.dataset.stringError || 'Non siamo riusciti a completare la prenotazione. Riprova.',
    };
  }

  /** @param {number} cents */
  #formatMoney(cents) {
    const amount = cents / 100;
    if (this.#money) return this.#money.format(amount);
    return amount.toFixed(2);
  }

  /**
   * @param {string} message
   * @param {'success' | 'error'} tone
   */
  #setFeedback(message, tone) {
    const feedback = this.refs.feedback;
    if (!feedback) return;
    feedback.textContent = message;
    feedback.dataset.tone = tone;
    feedback.removeAttribute('hidden');
  }

  #clearFeedback() {
    const feedback = this.refs.feedback;
    if (!feedback) return;
    feedback.textContent = '';
    feedback.setAttribute('hidden', '');
  }

  /** @param {string} message */
  #announce(message) {
    this.refs.liveRegion.textContent = message;
  }
}

/* ------------------------------------------------------------------ *
 * Parsing date
 * ------------------------------------------------------------------ */

const MONTH_NAMES = {
  it: ['gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno', 'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre'],
  en: ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'],
};

/**
 * Normalizza un valore di opzione in una chiave `YYYY-MM-DD`.
 * Accetta `2026-08-15`, `15/08/2026`, `15-08-2026`, `15.8.2026` e `15 agosto 2026`.
 *
 * @param {string} value
 * @returns {string | null}
 */
export function parseDateValue(value) {
  const input = String(value ?? '').trim().toLowerCase();
  if (!input) return null;

  const iso = input.match(/(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (iso) return build(Number(iso[1]), Number(iso[2]), Number(iso[3]));

  const european = input.match(/\b(\d{1,2})[/.\-](\d{1,2})[/.\-](\d{4})\b/);
  if (european) return build(Number(european[3]), Number(european[2]), Number(european[1]));

  const named = input.match(/\b(\d{1,2})[°\s]+([a-zàéèìòù]+)\s+(\d{4})\b/);
  if (named) {
    const month = findMonth(named[2]);
    if (month) return build(Number(named[3]), month, Number(named[1]));
  }

  return null;

  /**
   * @param {number} year
   * @param {number} month
   * @param {number} day
   */
  function build(year, month, day) {
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }
}

/**
 * @param {string} name
 * @returns {number | null}
 */
function findMonth(name) {
  for (const months of Object.values(MONTH_NAMES)) {
    const index = months.findIndex((month) => month.startsWith(name.slice(0, 3)));
    if (index >= 0) return index + 1;
  }
  return null;
}

/**
 * Trova l'indice dell'opzione che contiene le date: quella in cui almeno il 60%
 * dei valori distinti e' riconosciuto come data.
 *
 * @param {{ options: string[], variants: BookingVariant[], dateOptionIndex?: number }} payload
 * @returns {number}
 */
export function detectDateOptionIndex(payload) {
  if (typeof payload.dateOptionIndex === 'number' && payload.dateOptionIndex >= 0) return payload.dateOptionIndex;

  for (let index = 0; index < payload.options.length; index += 1) {
    const values = new Set(payload.variants.map((variant) => variant.options[index]).filter(Boolean));
    if (!values.size) continue;

    let parsed = 0;
    for (const value of values) if (parseDateValue(value)) parsed += 1;
    if (parsed / values.size >= 0.6) return index;
  }

  return -1;
}

/**
 * @param {Date} date
 * @returns {string}
 */
function toKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

if (!customElements.get('booking-calendar-component')) {
  customElements.define('booking-calendar-component', BookingCalendarComponent);
}
